import { every, isEmpty } from 'lodash';
import type {
  FilterAggregation as Agg,
  FilterAggregations,
  CheckboxFilterItem as FilterItem,
  FiltersBase,
} from 'types/filters';
import { i18n as I18n } from './i18n';
import { sortByName } from './sortBy';

export const checkedOrAggregated = (itemAggs: Agg[], item: FilterItem) => {
  return (
    item.checked ||
    itemAggs.some((itemAgg: Agg) => {
      return itemAgg.id.toString() === item.value!.toString();
    })
  );
};

export const isVisible = (item: FilterItem) => {
  return item.visible;
};

export const setVisibility = (filterItems: FilterItem[], aggs: Agg[]) => {
  return filterItems.map((filterItem: FilterItem) => {
    return {
      ...filterItem,
      visible: checkedOrAggregated(aggs, filterItem),
    };
  });
};

export const isChecked = (i: FilterItem) => {
  return i.checked;
};

export const aggItemsToCheckboxFilterItem = (aggItems: Agg) => {
  return {
    ...aggItems,
    value: aggItems.id,
    checked: false,
    applied: false,
    visible: true,
  };
};

export const isApplied = (item: FilterItem) => {
  return item.applied;
};

export const isEmptyFilters = (items: FilterItem[]) => {
  return isEmpty(items.filter(isVisible));
};

export const allVisibleSelected = (items: FilterItem[]) => {
  return every(items.filter(isVisible), 'checked');
};

const byName = (item: FilterItem) => {
  return item.name!;
};

export const appliedFilterNames = (items: FilterItem[]) => {
  return items.filter(isApplied).map(byName);
};

export const displayOptions = <T extends FilterItem>(items: T[]) => {
  return sortByName(items).filter(isVisible);
};

export const translatedNames = (items: FilterItem[], i18nLookup: string) => {
  return items.map((item: FilterItem) => {
    return {
      ...item,
      name: I18n.t(`${i18nLookup}.${item.value}`),
    };
  });
};

// Recalculates one group's visibility against the latest aggregations, except
// for the group the user most recently applied — leaving that one alone stops
// their own selection disappearing from under them.
// The group name is a type parameter so the filters argument is constrained to
// something that actually has a check-box group under that name.
export const dynamicFilter = <
  K extends string,
  F extends FiltersBase & Record<K, FilterItem[]>,
>(
  filterName: K,
  filters: F,
  aggs: FilterAggregations
) => {
  if (filters.latestAppliedFilter !== filterName) {
    return setVisibility(filters[filterName], aggs[filterName]);
  }

  return filters[filterName];
};
